/**
 * OE-266 (Paul, 2026-10-30): a business month starts the day after the previous one ends.
 *
 * `getYearlyMonthCycles` (the published @newgenus/every-moment) moves a weekend month START to the
 * nearest business day on its own, so with "previous business day" a month ending Sat 25 Apr (moved to
 * Fri 24) was followed by one starting Sun 26 Apr moved back to Fri 24: that day sat in both months
 * (2026: 24 Apr and 24 Jul). This realigns each month's business-day starts to follow the previous
 * month's business-day end, so no day is in two months or in none. The plain dates are untouched.
 *
 * Takes and returns the generator's output (an array of single-key objects, moment values), in place.
 * Weekends are the only non-business days, as in every-moment's default.
 *
 * The backend keeps a mirror (apps/newgenus-backend/src/shared/dateCycleMonths.ts) with a drift guard.
 */

/** The moment methods used (keeps this file free of a moment import). */
interface MomentLike {
    clone(): MomentLike;
    add(amount: number, unit: 'day'): MomentLike;
    day(): number;
    format(pattern: string): string;
}

interface MonthCycleValue {
    monthStart: {
        monthStartDate: MomentLike;
        monthStartPrevBusinessDate?: MomentLike;
        monthStartNextBusinessDate?: MomentLike;
        // Typed as Moment by every-moment's declarations, a "D-ddd" label at runtime.
        PrevBusinessDateStart?: unknown;
        NextBusinessDateStart?: unknown;
    };
    monthEnd: {
        monthEndDate: MomentLike;
        monthEndPrevBusinessDate?: MomentLike;
        monthEndNextBusinessDate?: MomentLike;
    };
}

const SATURDAY = 6;
const SUNDAY = 0;

function toBusinessDay(date: MomentLike, direction: 'prev' | 'next'): MomentLike {
    const d = date.clone();
    while (d.day() === SATURDAY || d.day() === SUNDAY) d.add(direction === 'prev' ? -1 : 1, 'day');
    return d;
}

export function alignBusinessMonthStarts<T extends Record<string, MonthCycleValue>>(cycles: T[]): T[] {
    let previous: MonthCycleValue | undefined;
    for (const cycle of cycles) {
        const month = Object.values(cycle)[0];
        if (!month?.monthStart?.monthStartDate) continue;
        // The previous month's end: from the list, or (for the first month) the day before this start.
        const plainPreviousEnd = month.monthStart.monthStartDate.clone().add(-1, 'day');
        const prevEnd = previous?.monthEnd.monthEndPrevBusinessDate ?? toBusinessDay(plainPreviousEnd, 'prev');
        const nextEnd = previous?.monthEnd.monthEndNextBusinessDate ?? toBusinessDay(plainPreviousEnd, 'next');
        month.monthStart.monthStartPrevBusinessDate = prevEnd.clone().add(1, 'day');
        month.monthStart.monthStartNextBusinessDate = nextEnd.clone().add(1, 'day');
        month.monthStart.PrevBusinessDateStart = month.monthStart.monthStartPrevBusinessDate.format('D-ddd');
        month.monthStart.NextBusinessDateStart = month.monthStart.monthStartNextBusinessDate.format('D-ddd');
        previous = month;
    }
    return cycles;
}
